/**
 * Product telemetry taxonomy — the reusable contract for user-journey events.
 *
 * Every instrumented flow emits PostHog events under a canonical
 * `module.flow.step` name (e.g. `ar.statement.invoice_exclusion_toggled`) and
 * carries `{ module, flow, step }` as properties. That lets a single PostHog
 * insight chart any flow's funnel by filtering on `flow`, or roll a whole
 * `module` up at once — without a bespoke table or per-flow plumbing.
 *
 * Onboarding a new flow:
 *   1. Add its module to `TelemetryModule` (if new).
 *   2. Register the flow + its step names as a `TelemetryFlowDefinition` here.
 *   3. Emit via `useFlowTelemetry` (frontend) or `trackFlowEvent` (backend).
 *
 * Frontend and backend both import from this module so their event names can
 * never drift apart.
 */

export enum TelemetryModule {
  AR = "ar",
  ITEM_MASTER = "item_master",
}

/** Canonical event name for a user-journey step: `module.flow.step`. */
export function telemetryEventName(
  module: TelemetryModule | string,
  flow: string,
  step: string,
): string {
  return `${module}.${flow}.${step}`;
}

/**
 * Properties stamped on every flow-telemetry event alongside the caller's own,
 * so events are queryable by `module` / `flow` / `step` regardless of the
 * flattened event name.
 */
export interface FlowTelemetryEnvelope {
  module: string;
  flow: string;
  step: string;
}

/**
 * A registered flow: its module, its flow id, and the closed set of step names
 * it emits. The generic preserves literal step keys/values so consumers get
 * autocomplete and typo-safety on `steps`.
 */
export interface TelemetryFlowDefinition<
  Steps extends Record<string, string> = Record<string, string>,
> {
  module: TelemetryModule;
  flow: string;
  steps: Steps;
}

/**
 * AR customer-statement build/send journey (the SendStatementDialog). Steps
 * trace the funnel: open → customize (exclude invoices / highlight rows / edit
 * subject+body) → send | abandon.
 *
 * `sent` / `send_failed` are emitted server-side (`trackFlowEvent` in the send
 * handler) so the outcome is recorded even if the browser tab closes; the rest
 * are client-side engagement steps (`useFlowTelemetry`) so we also capture
 * customization that never results in a send.
 */
export const AR_STATEMENT_TELEMETRY = {
  module: TelemetryModule.AR,
  flow: "statement",
  steps: {
    dialogOpened: "dialog_opened",
    invoiceExclusionToggled: "invoice_exclusion_toggled",
    highlightApplied: "highlight_applied",
    highlightsCleared: "highlights_cleared",
    subjectEdited: "subject_edited",
    bodyEdited: "body_edited",
    sendClicked: "send_clicked",
    sent: "sent",
    sendFailed: "send_failed",
    dialogAbandoned: "dialog_abandoned",
  },
} as const satisfies TelemetryFlowDefinition;

/**
 * Item Catalog advanced-filters apply journey. One client-side step fires when
 * the committed filter *shape* changes (columns + operators), so PostHog can
 * answer "which columns are used as filters" without capturing typed values.
 *
 * Emitted from the catalog search tab (Gently and AS/400 share that panel).
 * Filter *values* are intentionally omitted — they can be PII or business data.
 */
export const ITEM_CATALOG_TELEMETRY = {
  module: TelemetryModule.ITEM_MASTER,
  flow: "item_catalog",
  steps: {
    advancedFiltersApplied: "advanced_filters_applied",
  },
} as const satisfies TelemetryFlowDefinition;
